import { Box } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { updateReceipt } from "../_request";
import { useAuth } from "context";

const EditOrderDetails = ({ setStep, extractedData, selectedRestaurantData, setShowDoneModal }) => {
  const { showSnackbarWithMessage } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    restaurantName: "",
    restaurantAddress: "",
    orderId: "",
    deliveryDate: "",
    subtotalAmount: "",
    discount: "",
  });

  // Populate form with extracted data when component mounts or extractedData changes
  useEffect(() => {
    if (extractedData && extractedData.extractedData) {
      const data = extractedData.extractedData;

      // Convert cents to dollars for display
      const subtotalInDollars = data.foodSubTotalInCents
        ? (data.foodSubTotalInCents / 100).toFixed(2)
        : "";
      const discountInDollars = data.couponDiscountInCents
        ? (data.couponDiscountInCents / 100).toFixed(2)
        : "";

      // Format delivery date
      const deliveryDate = data.deliveryDate
        ? new Date(data.deliveryDate).toISOString().split("T")[0] // Output: "2023-10-01"
        : "";

      // Use selected restaurant data or fallback to defaults
      const restaurantName = selectedRestaurantData?.name || "";
      const restaurantAddress = selectedRestaurantData
        ? `${selectedRestaurantData.storeAddressLine1}${
            selectedRestaurantData.storeAddressLine2
              ? ", " + selectedRestaurantData.storeAddressLine2
              : ""
          }, ${selectedRestaurantData.city}, ${selectedRestaurantData.state}`
        : "";

      setFormData({
        restaurantName: restaurantName,
        restaurantAddress: restaurantAddress,
        orderId: data.orderID || "",
        deliveryDate: deliveryDate,
        subtotalAmount: subtotalInDollars,
        discount: discountInDollars,
      });
    }
  }, [extractedData, selectedRestaurantData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    if (!formData.orderId || !formData.deliveryDate || !formData.subtotalAmount) {
      showSnackbarWithMessage({
        type: "error",
        message: "Please fill in all required fields",
      });
      return;
    }
    setIsSubmitting(true);

    const mergedData = {
      ...extractedData.extractedData,
      orderID: formData.orderId,
      deliveryDate: formData.deliveryDate ? `${formData.deliveryDate}T00:00:00Z` : null,
      foodSubTotalInCents: formData.subtotalAmount
        ? Math.round(parseFloat(formData.subtotalAmount) * 100)
        : null,
      couponDiscountInCents: formData.discount
        ? Math.round(parseFloat(formData.discount) * 100)
        : null, // Convert discount to cents (if exists)
    };
    try {
      const {
        data: { success, message },
      } = await updateReceipt(extractedData?.id, { extractedData: mergedData });
      if (success) {
        setIsSubmitting(false);
        setShowDoneModal(true);
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (e) {
      showSnackbarWithMessage({
        type: "error",
        message: e.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <MDBox className="mb-8 flex items-center justify-between">
        <div className="text-[#344054] text-[17px] font-semibold mb-1">Receipt details</div>

        <button
          type="button"
          onClick={handleEdit}
          className="text-sm text-green-600 hover:text-green-700 font-medium cursor-pointer flex items-center justify-center gap-1"
        >
          {!isEditing && (
            <DriveFileRenameOutlineIcon sx={{ fontSize: "20px !important", color: "#16a34a" }} />
          )}

          {!isEditing && "Edit"}
        </button>
      </MDBox>

      <form onSubmit={handleSubmit}>
        <div className="gap-5 flex flex-col">
          {/* Restaurant Name - Always Disabled */}
          <MDBox>
            <p className="mb-[4px] text-sm font-medium text-[#414651]">Restaurant Name *</p>
            <MDInput
              fullWidth
              name="restaurantName"
              value={formData.restaurantName}
              disabled={true} // Always disabled
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#f9fafb",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  color: "#6b7280",
                  WebkitTextFillColor: "#6b7280",
                },
              }}
            />
          </MDBox>

          {/* Restaurant Address - Always Disabled */}
          <MDBox>
            <p className="mb-[4px] text-sm font-medium text-[#414651]">Restaurant address *</p>
            <MDInput
              fullWidth
              name="restaurantAddress"
              value={formData.restaurantAddress}
              disabled={true} // Always disabled
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#f9fafb",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  color: "#6b7280",
                  WebkitTextFillColor: "#6b7280",
                },
              }}
            />
          </MDBox>

          {/* Order ID */}
          <MDBox>
            <p className="mb-[4px] text-sm font-medium text-[#414651]">Order ID *</p>
            <MDInput
              fullWidth
              name="orderId"
              value={formData.orderId}
              onChange={(e) => handleInputChange("orderId", e.target.value)}
              disabled={!isEditing}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: !isEditing ? "#f9fafb" : "white",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  color: "#6b7280",
                  WebkitTextFillColor: "#6b7280",
                },
              }}
            />
          </MDBox>

          {/* Delivery Date */}
          <MDBox>
            <p className="mb-[4px] text-sm font-medium text-[#414651]">Delivery Date *</p>
            <MDInput
              fullWidth
              name="deliveryDate"
              type={isEditing ? "date" : "text"}
              value={formData.deliveryDate}
              onChange={(e) => handleInputChange("deliveryDate", e.target.value)}
              disabled={!isEditing}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: !isEditing ? "#f9fafb" : "white",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  color: "#6b7280",
                  WebkitTextFillColor: "#6b7280",
                },
              }}
            />
          </MDBox>

          {/* Subtotal Amount */}
          <MDBox>
            <p className="mb-[4px] text-sm font-medium text-[#414651]">Subtotal amount *</p>
            <div className="relative">
              <span className="absolute isolate z-[9] left-3 top-1/2 transform -translate-y-1/2 text-[#535862] text-[15px]">
                $
              </span>
              <MDInput
                fullWidth
                name="subtotalAmount"
                type="number"
                step="0.01"
                value={formData.subtotalAmount}
                onChange={(e) => handleInputChange("subtotalAmount", e.target.value)}
                disabled={!isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    paddingLeft: "24px",
                    backgroundColor: !isEditing ? "#f9fafb" : "white",
                  },
                  "& .MuiOutlinedInput-input": {
                    paddingLeft: "12px",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "#6b7280",
                    WebkitTextFillColor: "#6b7280",
                  },
                }}
                InputProps={{
                  inputProps: {
                    inputMode: "numeric",
                  },
                  sx: {
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                  },
                }}
              />
            </div>
          </MDBox>

          {/* Discount */}
          <MDBox>
            <p className="mb-[4px] text-sm font-medium text-[#414651]">Discount</p>
            <div className="relative">
              <span className="absolute z-[9] left-3 top-1/2 transform -translate-y-1/2 text-[#535862] text-[15px]">
                $
              </span>
              <MDInput
                fullWidth
                name="discount"
                type="number"
                step="0.01"
                value={formData.discount}
                onChange={(e) => handleInputChange("discount", e.target.value)}
                disabled={!isEditing}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    paddingLeft: "24px",
                    backgroundColor: !isEditing ? "#f9fafb" : "white",
                  },
                  "& .MuiOutlinedInput-input": {
                    paddingLeft: "12px",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "#6b7280",
                    WebkitTextFillColor: "#6b7280",
                  },
                }}
              />
            </div>
          </MDBox>
        </div>

        {/* Terms and Conditions */}
        <div className="mt-4">
          <p className="!text-sm text-[#475467]">
            By clicking submit, you agree to our{" "}
            <span className="text-green-600 font-medium hover:underline cursor-pointer">
              Terms & Conditions
            </span>{" "}
            and acknowledge our{" "}
            <span className="text-green-600 font-medium hover:underline cursor-pointer">
              Privacy Policy
            </span>
            .
          </p>
        </div>

        {/* BACK AND SUBMIT BTN  */}
        <Box
          display="flex"
          className="gap-2 md:gap-4"
          alignItems="center"
          style={{ marginTop: "30px" }}
        >
          <MDButton
            fullWidth
            variant="outlined"
            color="black"
            onClick={() => setStep(0)}
            disabled={isSubmitting}
            sx={{
              borderRadius: "8px",
              padding: "10px",
              textTransform: "none",
              border: "1px solid #D0D5DD",
              textDecoration: "lowercase",
              fontSize: 14,
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            }}
          >
            Back
          </MDButton>

          <MDButton
            type="submit"
            sx={{ textTransform: "none", fontSize: 14 }}
            fullWidth
            variant="contained"
            color="success"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : isEditing ? "Save & Submit" : "Submit"}
          </MDButton>
        </Box>
      </form>
    </div>
  );
};

export default EditOrderDetails;

EditOrderDetails.propTypes = {
  setStep: PropTypes.func.isRequired,
  extractedData: PropTypes.object,
  selectedRestaurantData: PropTypes.object,
  setShowDoneModal: PropTypes.any,
};
